import { isHttpSecure } from "lib/utils/utils";
import { joinUrl } from "lib/utils/connection";
import { gatewayServiceBaseUrl } from "lib/consts/arcGateway";
import { authedFetch } from "lib/utils/authedFetch";
import { throwUserFacingHttpError } from "lib/utils/userFacingApiError";

/** HTTP API for WiFi scan on the robot (FastAPI + nmcli), default port 12394. */

export type WifiNetwork = {
    ssid: string;
    signal: number | null;
    security: string | null;
};

/** One row per SSID; keep the strongest signal (nmcli lists one row per BSSID). */
export function dedupeWifiNetworksBySsid(networks: WifiNetwork[]): WifiNetwork[] {
    const best = new Map<string, WifiNetwork>();
    for (const n of networks) {
        const key = n.ssid.trim();
        if (!key) continue;
        const existing = best.get(key);
        if (
            existing == null ||
            (n.signal != null && (existing.signal == null || n.signal > existing.signal))
        ) {
            best.set(key, n);
        }
    }
    return Array.from(best.values());
}

export type WifiScanResponse = {
    interface: string;
    networks: WifiNetwork[];
};

export type WifiStatusResponse = {
    interface: string;
    state?: string | null;
    connection?: string | null;
    ssid?: string | null;
    error?: string | null;
};

export type WifiConnectRequest = {
    ssid: string;
    password?: string;
    ipv4_method: "auto" | "manual";
    ipv4_address?: string | null;
    ipv4_gateway?: string | null;
};

export type WifiConnectResponse = {
    ok: boolean;
    message: string;
};

export type WifiSavedCredentialsResponse = {
    ssid: string;
    password?: string | null;
    has_password: boolean;
};

const DEFAULT_WIFI_API_PORT = "12394";

/** Base URL with no trailing slash. Pass `selectedArcHost` from `useARC().selectedARC?.host` when available. */
export function getWifiApiBaseUrl(selectedArcHost?: string | null, isSecure: boolean = isHttpSecure(), arcId?: string | null): string | null {
    if (process.env.REACT_APP_WIFI_API_DISABLED === "true") {
        return null;
    }
    const explicit = process.env.REACT_APP_WIFI_API_BASE_URL?.trim();
    if (explicit) {
        return explicit.replace(/\/$/, "");
    }
    const port = process.env.REACT_APP_WIFI_API_PORT?.trim() || DEFAULT_WIFI_API_PORT;
    const proxyHost =
        selectedArcHost?.trim() ||
        process.env.REACT_APP_WIFI_API_HOST?.trim();
    const host =
        proxyHost ||
        process.env.REACT_APP_SINGLE_ARC_HOSTNAME?.trim() ||
        process.env.REACT_APP_HOST_OVERRIDE?.trim() ||
        (typeof window !== "undefined" ? window.location.hostname : "");
    if (!host) {
        return null;
    }
    const protocol = isSecure ? "https" : "http";
    // Direct mode uses arc_gateway; fleet mode uses the proxy's path-routed
    // origin. A fleet ARC without a proxy host is unavailable.
    const gatewayBase = gatewayServiceBaseUrl("wifi", arcId, proxyHost);
    if (gatewayBase) return gatewayBase;
    if (arcId) return null;

    return `${protocol}://${host}:${port}`;
}

export async function fetchWifiScan(baseUrl: string): Promise<WifiScanResponse> {
    const url = joinUrl(baseUrl, "/wifi/scan");
    const res = await authedFetch(url);
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't scan for Wi-Fi networks. Try again.");
    return res.json() as Promise<WifiScanResponse>;
}

export async function fetchWifiStatus(baseUrl: string): Promise<WifiStatusResponse> {
    const url = joinUrl(baseUrl, "/wifi/status");
    const res = await authedFetch(url);
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't load Wi-Fi status. Try again.");
    return res.json() as Promise<WifiStatusResponse>;
}

export async function postWifiConnect(
    baseUrl: string,
    body: WifiConnectRequest
): Promise<WifiConnectResponse> {
    const url = joinUrl(baseUrl, "/wifi/connect");
    const res = await authedFetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            ssid: body.ssid,
            password: body.password ?? "",
            ipv4_method: body.ipv4_method,
            ipv4_address: body.ipv4_address?.trim() || null,
            ipv4_gateway: body.ipv4_gateway?.trim() || null,
        }),
    });
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't connect to the Wi-Fi network. Try again.");
    return res.json() as Promise<WifiConnectResponse>;
}

export async function postWifiDisconnect(baseUrl: string): Promise<WifiConnectResponse> {
    const url = joinUrl(baseUrl, "/wifi/disconnect");
    const res = await authedFetch(url, { method: "POST" });
    if (!res.ok) await throwUserFacingHttpError(res, "Couldn't disconnect from Wi-Fi. Try again.");
    return res.json() as Promise<WifiConnectResponse>;
}

/** Saved PSK from NetworkManager for a previously joined SSID (404 when none). */
export async function fetchWifiSavedCredentials(
    baseUrl: string,
    ssid: string
): Promise<WifiSavedCredentialsResponse> {
    const url = joinUrl(
        baseUrl,
        `/wifi/saved-credentials?ssid=${encodeURIComponent(ssid)}`
    );
    const res = await authedFetch(url);
    if (res.status === 404) {
        throw new Error("no saved connection");
    }
    if (!res.ok) {
        await throwUserFacingHttpError(
            res,
            "Couldn't load saved Wi-Fi credentials. Try again."
        );
    }
    return res.json() as Promise<WifiSavedCredentialsResponse>;
}
